import { makeStyles } from "tss-react/mui"
import { Grid, Stack, Typography } from "@mui/material"

import { DateTime } from "luxon"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { Box } from "@mui/system"
import {
    fetchMeetingServiceOrders,
    fetchMeetingServiceOrdersForExport,
    MeetingServiceOrdersForExportResponse,
    MeetingServiceOrdersResponse,
    MerchantMeetingServiceOrdersQuery,
} from "../meetingServiceApi"
import { useEffect, useMemo, useState } from "react"

import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { SharePrintButtons } from "../../../../../nonAdminUser/reports/MeetingsReceipt/components/SharePrintButtons"
import GeneralErrorAnim from "../../../../../../shared/assets/general-error-lottie-anim.json"
import { ModalHeader } from "./deliveryListModal/ModalHeader"
import {
    DeliveryFilterValue,
    GroupBy,
    MeetingServiceOrderStatus,
    getDeliveryTypeFilterKey,
    getDeliveryFilterFromKey,
    getTimePeriod,
    TimePeriodMeetingService,
    DELIVERY_FILTER_ALL,
} from "../meetingServiceTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { ErrorView } from "../../../../../../shared/components/errors/ErrorView"
import { ErrorFallback } from "../../../../../receipt/components/ErrorFallback"
import { groupOrdersByDateAndType, resolveDeliveryDateTime } from "./deliveryListModal/helpers"
import { DeliveryGroups } from "./deliveryListModal/DeliveryGroups"
import { DeliveryListHeader } from "./deliveryListModal/DeliveryListHeader"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { OrderChatHistoryPage } from "../../../../../../shared/components/orderChat/print/OrderChatHistoryPage"
import { usePrintWithChatHistory } from "../../../../../../shared/components/orderChat/print/usePrintWithChatHistory"
import type { RenderPrintViewOrderMenu } from "../../../components/receiptAndSlip/PrintViewOrderActions"
import { HIDDEN_IN_PRINT, VISIBLE_ONLY_IN_PRINT } from "../../../../../../shared/components/print/printPageOnly"
import { ContactInfoDisclaimer } from "../../../../../../shared/components/receipt/ContactInfoDisclaimer"
import { PrintPageHeaderFooter } from "../../../../../../shared/components/print/PrintPageHeaderFooter"
import { getContactInfo, getKitchenNames, hasContactDetails } from "./slipModal/utils"

const useStyles = makeStyles()((theme) => ({
    container: {
        backgroundColor: tailwindPalette.slate[100],
        minHeight: "100vh",
        gap: "3.5rem",
        paddingBottom: "3.5rem",
        justifyContent: "start",
        [theme.breakpoints.down("sm")]: {
            backgroundColor: theme.palette.common.white,
            gap: 0,
        },
        "@media print": {
            backgroundColor: "transparent",
            minHeight: "auto",
            width: "100vw",
            paddingBottom: 0,
        },
    },
    slideContainer: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
    },
    slide: {
        width: "80%",
        backgroundColor: "white",
        boxShadow: "0px 0px 10px 0px #00000026;",
        paddingBottom: "3.5rem",
        "@media print": {
            width: "100%",
            display: "block",
            boxShadow: "none",
            backgroundColor: "white",
            paddingBottom: 0,
        },
    },
    header: {
        padding: "1.25rem 1.5rem",
        position: "sticky",
        top: 0,
        zIndex: 1,
        maxHeight: "4.5rem",
        backgroundColor: "white",
        boxShadow: "0px 2px 6px 0px #00000014",
        "@media print": {
            display: "none",
        },
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            padding: "0 1.5rem",
            zIndex: 1,
            maxHeight: "auto",
            boxShadow: "0px 3px 1px -2px #00000033,  0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F",
        },
    },
    actionButtons: {
        justifySelf: "end",
        flex: "none",
    },
    emptyState: {
        textAlign: "center",
        padding: "4rem 2rem",
    },
    failedOrders: {
        padding: "0 1.5rem 2rem",
        "@media print": {
            padding: "0 0 2rem",
        },
    },
    hiddenInPrint: {
        ...HIDDEN_IN_PRINT,
    },
    visibleOnlyInPrint: {
        ...VISIBLE_ONLY_IN_PRINT,
    },
}))

export type DeliveryListMode = "selected" | "all"
export type DeliveryListDateState = {
    startDate: DateTime | null
    endDate: DateTime | null
}
export type MeetingServiceOrdersDependencies = Parameters<typeof fetchMeetingServiceOrders>[0]

type StartPageLayoutProps = Readonly<{
    open: boolean
    onClose: () => void
    mode: DeliveryListMode
    orderIds?: number[]
    meetingServiceDependencies: MeetingServiceOrdersDependencies
    tab?: MeetingServiceOrderStatus
    renderOrderMenu?: RenderPrintViewOrderMenu
}>

const selectOrdersForExport = ({ orders }: MeetingServiceOrdersForExportResponse) => ({
    orders: orders.filter((order) => !order.serviceResponse),
    failedOrders: orders.filter((order) => order.serviceResponse),
})
const selectMeetingService = (data: MeetingServiceOrdersResponse) => data?.orders || []

export function OrderDeliveryList({
    mode,
    orderIds,
    meetingServiceDependencies,
    tab,
    open,
    onClose,
    renderOrderMenu,
}: StartPageLayoutProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { merchantId, view, period, startDeliveryDate, endDeliveryDate, deliveryType, ...rest } =
        meetingServiceDependencies

    // Calculate initial dates from period if available, otherwise use provided date range
    const periodDates = period ? getTimePeriod(dateTimeFactory, period) : null
    const initStartDate =
        periodDates?.start ??
        (startDeliveryDate ? dateTimeFactory.fromISO(startDeliveryDate) : null) ??
        dateTimeFactory.now().startOf("day")
    const initEndDate =
        periodDates?.end ??
        (endDeliveryDate ? dateTimeFactory.fromISO(endDeliveryDate) : null) ??
        dateTimeFactory.now().endOf("day")

    const userId = useAppSelector(selectEffectiveUserId) || 0
    const [groupBy, setGroupBy] = useState<GroupBy>("location")
    const [deliveryTypeState, setDeliveryTypeState] = useState<DeliveryFilterValue>(() =>
        getDeliveryFilterFromKey(deliveryType),
    )

    const isAuthenticated = Boolean(userId)
    const isSelectMode = mode === "selected"

    const [dateState, setDateState] = useState<DeliveryListDateState>({
        startDate: initStartDate,
        endDate: initEndDate,
    })
    const [hasCustomDateRange, setHasCustomDateRange] = useState(false)

    const handleDateStateChange = (newValue: { startDate: DateTime; endDate: DateTime }) => {
        setDateState(newValue)
        setHasCustomDateRange(true)
    }

    const showPeriodAll =
        period === TimePeriodMeetingService.ALL && !hasCustomDateRange && !startDeliveryDate && !endDeliveryDate

    const excludeDateTime = [MeetingServiceOrderStatus.NOT_APPROVED, MeetingServiceOrderStatus.TO_BE_BOOKED].some(
        (t) => t === tab,
    )

    // Determine date range for API query
    // Explicit dates (custom range or date picker from table) take priority over period.
    // period=ALL is only sent when there are no explicit dates (user selected "All" from period picker).
    const queryDateRange = hasCustomDateRange
        ? {
              startDeliveryDate: dateState.startDate?.toISODate() ?? undefined,
              endDeliveryDate: dateState.endDate?.toISODate() ?? undefined,
          }
        : startDeliveryDate || endDeliveryDate
          ? { startDeliveryDate, endDeliveryDate }
          : { period }

    const {
        data: allOrders = [],
        isFetching: isFetchingAllOrders,
        error: allOrdersError,
    } = useApiQuery(fetchMeetingServiceOrders, {
        queryName: MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
        dependencies: {
            ...rest,
            merchantId,
            view,
            deliveryType: getDeliveryTypeFilterKey(deliveryTypeState),
            ...(!excludeDateTime ? queryDateRange : {}),
        },
        enabled: isAuthenticated && !isSelectMode,
        select: selectMeetingService,
    })

    // a recurring order comes back as one list row per occurrence
    const exportOrderIds = [...new Set(isSelectMode ? orderIds : allOrders.map((order) => order.id))]

    const {
        data: exportData,
        isFetching: isFetchingOrdersForExport,
        error: exportOrdersError,
    } = useApiQuery(fetchMeetingServiceOrdersForExport, {
        queryName: MerchantMeetingServiceOrdersQuery.ORDERS_FOR_EXPORT,
        dependencies: {
            orderIds: exportOrderIds,
        },
        enabled: isAuthenticated && Boolean(exportOrderIds?.length),
        select: selectOrdersForExport,
    })

    const orders = useMemo(() => {
        const exportOrders = exportData?.orders ?? []
        if (isSelectMode) return exportOrders

        // list rows refresh after a chat message is sent, the batch result doesn't
        return exportOrders.map((order) => ({
            ...order,
            messages: allOrders.find((row) => row.id === order.id)?.messages ?? order.messages,
        }))
    }, [exportData, allOrders, isSelectMode])
    const failedOrders = exportData?.failedOrders ?? []
    const isFetching = (!isSelectMode && isFetchingAllOrders) || isFetchingOrdersForExport
    const loadError = allOrdersError || exportOrdersError

    const filteredOrders = useMemo(() => {
        if (!isSelectMode) return orders

        return orders.filter((order) => {
            if (deliveryTypeState !== DELIVERY_FILTER_ALL) {
                if (order.deliveryDetails.type !== deliveryTypeState) return false
            }

            if (hasCustomDateRange && dateState.startDate && dateState.endDate) {
                const rawDateTime = resolveDeliveryDateTime(order)
                if (rawDateTime) {
                    const dt = dateTimeFactory.fromISO(rawDateTime)
                    if (dt.isValid) {
                        const start = dateState.startDate.startOf("day")
                        const end = dateState.endDate.endOf("day")
                        if (dt < start || dt > end) return false
                    }
                }
            }

            return true
        })
    }, [isSelectMode, orders, deliveryTypeState, hasCustomDateRange, dateState, dateTimeFactory])

    const grouped = useMemo(
        () => groupOrdersByDateAndType(filteredOrders, groupBy, dateTimeFactory),
        [filteredOrders, groupBy, dateTimeFactory],
    )

    useEffect(() => {
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = ""
        }
    }, [])

    const hasNoOrders = !isFetching && !loadError && filteredOrders.length === 0 && failedOrders.length === 0
    const hasContactInfo = filteredOrders.some((order) =>
        hasContactDetails(getContactInfo(order.organizers, order.customer)),
    )
    const { isChatHistoryPrinted, printedSingleOrder, getOrderPrintActions, printWithChatHistory } =
        usePrintWithChatHistory(filteredOrders.map((order) => order.id))
    const printedOrder = filteredOrders.find((order) => order.id === printedSingleOrder?.orderId)
    const listTitle = l10n.getString("meeting-service-delivery-list")

    const content = loadError ? (
        <ErrorView
            lottieAnimation={GeneralErrorAnim}
            title="A problem occurred"
            message="Failed to load"
            buttonLabel="Close"
            onClose={onClose}
        />
    ) : (
        <Stack direction="column" className={classes.container}>
            <PrintPageHeaderFooter
                title={listTitle}
                kitchen={getKitchenNames(printedOrder ? [printedOrder] : filteredOrders)}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center" className={classes.header}>
                <ModalHeader
                    dateState={dateState}
                    setDateState={handleDateStateChange}
                    isSelectMode={isSelectMode}
                    deliveryType={deliveryTypeState}
                    setDeliveryType={setDeliveryTypeState}
                    groupBy={groupBy}
                    setGroupBy={setGroupBy}
                    showPeriodAll={showPeriodAll}
                />

                <Box className={classes.actionButtons}>
                    <SharePrintButtons
                        onPrintWithChatHistory={printWithChatHistory}
                        onClose={onClose}
                        hasShare={false}
                        hasClose={true}
                    />
                </Box>
            </Stack>
            {isFetching ? (
                <Grid container direction="column" component="main" alignItems="center" justifyContent="center">
                    <LoadingSpinner />
                </Grid>
            ) : (
                <Box id="printable-content" className={classes.slideContainer}>
                    <Stack direction="column" spacing={hasContactInfo ? "1.25rem" : "2rem"} className={classes.slide}>
                        <DeliveryListHeader dateState={dateState} label={listTitle} showPeriodAll={showPeriodAll}>
                            {hasContactInfo && <ContactInfoDisclaimer />}
                        </DeliveryListHeader>

                        {hasNoOrders ? (
                            <Box className={classes.emptyState}>
                                <LocalizedStrict id="meeting-service-delivery-list-empty">
                                    <Typography variant="h6">No delivery orders for the selected criteria</Typography>
                                </LocalizedStrict>
                            </Box>
                        ) : (
                            <Box className={cx(printedOrder && classes.hiddenInPrint)}>
                                {failedOrders.length > 0 && (
                                    // backend message names the wrong order in a batch, so the default text is used
                                    <Box className={classes.failedOrders}>
                                        {failedOrders.map((order) => (
                                            <ErrorFallback key={order.id} orderId={order.id} />
                                        ))}
                                    </Box>
                                )}
                                <DeliveryGroups
                                    groupBy={groupBy}
                                    grouped={grouped}
                                    dateTimeFactory={dateTimeFactory}
                                    renderOrderMenu={
                                        renderOrderMenu &&
                                        ((orderId) =>
                                            renderOrderMenu(
                                                orderId,
                                                getOrderPrintActions(orderId),
                                                allOrders.find((order) => order.id === orderId),
                                            ))
                                    }
                                />
                            </Box>
                        )}

                        {printedOrder && (
                            <Box className={classes.visibleOnlyInPrint}>
                                <DeliveryGroups
                                    groupBy={groupBy}
                                    grouped={groupOrdersByDateAndType([printedOrder], groupBy, dateTimeFactory)}
                                    dateTimeFactory={dateTimeFactory}
                                />
                                {printedSingleOrder?.withChatHistory && (
                                    <OrderChatHistoryPage orderId={printedOrder.id} />
                                )}
                            </Box>
                        )}

                        {isChatHistoryPrinted &&
                            filteredOrders.map((order) => <OrderChatHistoryPage key={order.id} orderId={order.id} />)}
                    </Stack>
                </Box>
            )}
        </Stack>
    )

    return (
        <StandardDialog open={open} onClose={onClose} isFullScreen hideCloseIcon topbar={<></>}>
            {content}
        </StandardDialog>
    )
}
